import i18next from 'i18next';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useRedirect } from '~hooks/useRedirect';
import { useRut } from '~app/hooks/useRut';
import { useFlowChoice } from '~app/hooks/useFlowChoice';
import { ROUTES } from '~constants/routes';
import { INCUMBENT_TYPES } from '~constants/incumbent';
import SimulationActions from '~redux/Simulation/actions';
import { SIMULATION_TARGETS } from '~redux/Simulation/constants';
import { mapAllDataScraperValues } from '~models/scrapers';
import SimulationNewFlowWrapper from '~components/SimulationNewFlowWrapper';
import Button from '~components/Button';
import Input from '~components/Input';
import { login, verify } from '~services/ClaveUnicaLoginService';
import { ReactComponent as ClaveUnicaLogo } from '~assets/unique.svg';

import UniqueKeyLoading from '../UniqueKeyLoading';

import ManualExit from './ManualExit';
import SecurityCard from './SecurityCard';
import { showLoginSuccessToast } from './loginSuccessToast';
import { useCodeCountdown } from './useCodeCountdown';
import {
  CLAVE_UNICA_PORTAL,
  CODE_LENGTH,
  CODE_WINDOW_SECONDS,
  MAX_CREDENTIAL_ERRORS,
  MAX_SERVICE_ERRORS,
  STEPS,
  formatTimeLeft
} from './constants';
import styles from './styles.module.scss';

// Native ClaveÚnica login: the user types RUN + password (and the 2FA code when
// asked) in our own form; kayak and the scraper log into ClaveÚnica. The user
// only waits until we are in: the data extraction runs in the background.
function ClientUniqueKeyLogin() {
  const dispatch = useDispatch();
  const { quickCode } = useParams();
  const handleRedirect = useRedirect();
  const currentRut = useRut(INCUMBENT_TYPES.CLIENT);
  const { clientFlowChoice } = useSelector(state => state.simulation);
  const { handleManualChoice } = useFlowChoice({
    incumbent: INCUMBENT_TYPES.CLIENT,
    currentFlowChoice: clientFlowChoice,
    getStepNameByFlowChoice: () => ROUTES.EVALUATION_PENDING.stepName
  });
  const [step, setStep] = useState(STEPS.CREDENTIALS);
  // null until the user edits the field, so the simulation's RUN (which may load
  // after the first render) prefills it.
  const [typedRun, setTypedRun] = useState(null);
  // Normalized to arc's format (12345678-K), since arc only applies the scraped
  // data when the RUN matches the client's.
  const run = (typedRun ?? currentRut ?? '').replace(/[.\s]/g, '').toUpperCase();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [credentialErrors, setCredentialErrors] = useState(0);
  // Blocked account or too many wrong codes: only the manual path is left.
  const [locked, setLocked] = useState(false);
  // One retry covers a passing failure (proxy 403, network); a second one means
  // ClaveÚnica or the scraper is down, so the manual path takes over.
  const [serviceErrors, setServiceErrors] = useState(0);
  const closed = locked || serviceErrors >= MAX_SERVICE_ERRORS;

  // eslint-disable-next-line no-use-before-define
  const [secondsLeft, setSecondsLeft] = useCodeCountdown({ loading, onExpire: () => expireCode() });

  const expireCode = () => {
    setCode('');
    setSecondsLeft(null);
    setStep(STEPS.CREDENTIALS);
    setError('errorExpired');
  };

  // Like the legacy scraper, record that ClaveÚnica was used (Pipedrive, arc), then
  // continue to the certificates (still required) with a toast; the extraction
  // runs in the background.
  const completeLogin = () => {
    setLoading(true);
    dispatch(
      SimulationActions.changeIncumbent({
        quickCode,
        values: mapAllDataScraperValues(INCUMBENT_TYPES.CLIENT),
        postSuccessAction: () => {
          dispatch(SimulationActions.setSimulationInfo(SIMULATION_TARGETS.CLIENT_UNIQUE_KEY_SUBMITTED, true));
          showLoginSuccessToast();
          handleRedirect({ customStepName: ROUTES.CLIENT_INCOMES.stepName })();
        }
      })
    );
  };

  // eslint-disable-next-line complexity
  const handleResponse = response => {
    const failService = () => {
      const failures = serviceErrors + 1;
      setServiceErrors(failures);
      setError(failures >= MAX_SERVICE_ERRORS ? 'errorServiceDown' : 'errorService');
    };
    if (!response.ok) {
      failService();
      return;
    }
    const { status, attemptsLeft: left } = response.data;
    setAttemptsLeft(left ?? null);
    switch (status) {
      case 'ok':
        completeLogin();
        break;
      case 'totp_required':
        setError(null);
        setStep(STEPS.CODE);
        setSecondsLeft(CODE_WINDOW_SECONDS);
        break;
      case 'invalid_code':
        // Every attempt renews the session on kayak and ClaveÚnica.
        setCode('');
        setError('errorInvalidCode');
        setSecondsLeft(CODE_WINDOW_SECONDS);
        break;
      case 'invalid_credentials':
        setCredentialErrors(count => count + 1);
        setError('errorInvalidCredentials');
        break;
      case 'blocked':
        setLocked(true);
        setError('errorBlocked');
        break;
      case 'too_many_attempts':
        setLocked(true);
        setError('errorTooManyAttempts');
        break;
      case 'expired':
        expireCode();
        break;
      case 'busy':
        break;
      default:
        // captcha_required, unavailable or anything new: ClaveÚnica could not be reached.
        failService();
    }
  };

  const handleCredentials = async event => {
    event.preventDefault();
    setLoading(true);
    const response = await login({ run, password, quickCode: `${quickCode}-${INCUMBENT_TYPES.CLIENT}` });
    // The password is not kept in memory once it has been sent.
    setPassword('');
    setLoading(false);
    handleResponse(response);
  };

  const handleCode = async event => {
    event.preventDefault();
    setLoading(true);
    const response = await verify({ code, quickCode: `${quickCode}-${INCUMBENT_TYPES.CLIENT}` });
    setLoading(false);
    handleResponse(response);
  };

  const renderError = () =>
    error && (
      <span className={styles.error} role="alert">
        {i18next.t(`ClientUniqueKeyLogin:${error}`)}
      </span>
    );

  const renderManualExit = () =>
    !closed &&
    (credentialErrors >= MAX_CREDENTIAL_ERRORS || error === 'errorService') && (
      <ManualExit handleManualChoice={handleManualChoice} />
    );

  // Once ClaveÚnica can't work (blocked, too many codes or down), the manual
  // path replaces the submit button inside the panel, where the user is looking.
  const renderSubmit = (label, disabled) =>
    closed ? (
      <ManualExit primary handleManualChoice={handleManualChoice} />
    ) : (
      <Button primary className={styles.claveUnicaButton} type="submit" disabled={disabled}>
        {i18next.t(`ClientUniqueKeyLogin:${label}`)}
      </Button>
    );

  const renderCredentials = () => (
    <form className={`column ${styles.content}`} onSubmit={handleCredentials} autoComplete="on">
      <div className={`column center ${styles.top}`}>
        <div className={styles.panel}>
          <ClaveUnicaLogo className={styles.logo} role="img" aria-label="ClaveÚnica" />
          <span className={styles.title}>{i18next.t('ClientUniqueKeyLogin:title')}</span>
          <Input
            inputContainerClassName={styles.claveUnicaInput}
            name="run"
            label={i18next.t('ClientUniqueKeyLogin:runLabel')}
            value={run}
            handleChange={({ target }) => setTypedRun(target.value)}
            autoComplete="username"
            disabled={closed}
          />
          <Input
            inputContainerClassName={styles.claveUnicaInput}
            name="password"
            type="password"
            label={i18next.t('ClientUniqueKeyLogin:passwordLabel')}
            value={password}
            handleChange={({ target }) => setPassword(target.value)}
            autoComplete="current-password"
            disabled={closed}
          />
          <a className={styles.helpLink} href={CLAVE_UNICA_PORTAL} target="_blank" rel="noreferrer">
            {i18next.t('ClientUniqueKeyLogin:helpLink')}
          </a>
          {renderError()}
          {renderSubmit('submit', !run || !password || loading)}
        </div>
        <SecurityCard />
      </div>
      <div className={`column center ${styles.bottom}`}>
        {renderManualExit()}
        <p className={styles.consent}>
          {i18next.t('PrivacyPolicies:firstPart')}
          <a
            className={styles.consentLink}
            href={process.env.REACT_APP_PRIVACY_POLICIES_LINK}
            target="_blank"
            rel="noreferrer">
            {i18next.t('PrivacyPolicies:privacyPolicies')}
          </a>
          {i18next.t('PrivacyPolicies:secondPart')}
          <a
            className={styles.consentLink}
            href={process.env.REACT_APP_EVALUATION_POLICIES_LINK}
            target="_blank"
            rel="noreferrer">
            {i18next.t('PrivacyPolicies:evaluationPolicies')}
          </a>
        </p>
      </div>
    </form>
  );

  const renderCode = () => (
    <form className={`column ${styles.content}`} onSubmit={handleCode}>
      <div className={`column center ${styles.top}`}>
        <div className={styles.panel}>
          <ClaveUnicaLogo className={styles.logo} role="img" aria-label="ClaveÚnica" />
          <span className={styles.title}>{i18next.t('ClientUniqueKeyLogin:codeTitle')}</span>
          <span className={styles.subtitle}>{i18next.t('ClientUniqueKeyLogin:codeSubtitle')}</span>
          <Input
            inputContainerClassName={styles.claveUnicaInput}
            name="code"
            label={i18next.t('ClientUniqueKeyLogin:codeLabel')}
            value={code}
            handleChange={({ target }) => setCode(target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
            inputMode="numeric"
            autoComplete="one-time-code"
            disabled={closed}
          />
          {renderError()}
          {!closed && secondsLeft !== null && (
            <span className={styles.hint} aria-live="polite">
              {i18next.t('ClientUniqueKeyLogin:codeTimeLeft', {
                time: formatTimeLeft(Math.max(secondsLeft, 0))
              })}
            </span>
          )}
          {!closed && attemptsLeft === 1 && (
            <span className={styles.hint}>{i18next.t('ClientUniqueKeyLogin:lastAttempt')}</span>
          )}
          {renderSubmit('verify', code.length !== CODE_LENGTH || loading)}
        </div>
      </div>
      <div className={`column center ${styles.bottom}`}>{renderManualExit()}</div>
    </form>
  );

  return (
    <SimulationNewFlowWrapper
      previousStepName={ROUTES.CLIENT_FLOW_CHOICE.stepName}
      disabled={loading}
      loading={false}>
      <UniqueKeyLoading loading={loading}>
        {step === STEPS.CODE ? renderCode() : renderCredentials()}
      </UniqueKeyLoading>
    </SimulationNewFlowWrapper>
  );
}

export default ClientUniqueKeyLogin;
